import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { EXAM_TRACK_GRADES, GRADE_GROUPS } from "@/lib/subjects";
import { ClassStudentSelect } from "@/components/ClassStudentSelect";
import {
  ClipboardList, LayoutGrid, PencilLine, ClipboardCheck, FileText, BookText,
  FolderOpen, CalendarCheck2, Library, Trophy, Database, ChevronRight, UserRound, Users,
  ListTree, Mountain, ScanLine, Building2,
} from "lucide-react";

const MODULES = [
  { to: "/admin/plan", label: "Haftalık Plan", desc: "Haftalık ders planı girişi (öğrenci veya tüm sınıf)", icon: ClipboardList, color: "bg-sky-100 text-sky-600", testid: "mod-weekly-plan" },
  { to: "/admin/calisma-programi", label: "Çalışma Programı", desc: "Haftalık kaynak programı & D/Y/B sonuç girişi", icon: LayoutGrid, color: "bg-emerald-100 text-emerald-600", testid: "mod-study-program" },
  { to: "/admin/program-arsivi", label: "Program Arşivi", desc: "Öğrencilere atanmış programlar yayınevi halinde toplu görünüm", icon: FolderOpen, color: "bg-violet-100 text-violet-600", testid: "mod-program-archive" },
  { to: "/gunluk-soru", label: "Günlük Soru", desc: "Ders bazlı D/Y/B soru girişi (LGS neti)", icon: PencilLine, color: "bg-amber-100 text-amber-600", testid: "mod-daily" },
  { to: "/konu-takibi", label: "Konu Takibi", desc: "Konu durumları (başlanmadı/çalışılıyor/iyi/bitti) ve kaynak girişi", icon: ListTree, color: "bg-teal-100 text-teal-600", testid: "mod-konu-takibi" },
  { to: "/matematik-kampi", label: "Matematik Kampı", desc: "5 kur sistemi, kur kilidi ve kaynak takibi", icon: Mountain, color: "bg-lime-100 text-lime-600", testid: "mod-matematik-kampi" },
  { to: "/optik-sinav", label: "Canlı Optik Sınav", desc: "Optik işaretle, cevap anahtarı/kazanım, karne & sınıflar arası sonuç", icon: ScanLine, color: "bg-fuchsia-100 text-fuchsia-600", testid: "mod-optik-sinav" },
  { to: "/optik-sinav", label: "Kurum Sınavı", desc: "Çok haftalı kurum serisi: cevap anahtarı, optik/toplu işaretleme, hafta sonucu & karne", icon: Building2, color: "bg-indigo-100 text-indigo-600", testid: "mod-kurum-sinav" },
  { to: "/sinav-takip", label: "Sınav Takip (4 & 7)", desc: "4 & 7. sınıf özel sınav sonuç tabloları", icon: ClipboardCheck, color: "bg-rose-100 text-rose-600", testid: "mod-sinav-takip", grades: EXAM_TRACK_GRADES },
  { to: "/deneme-karti", label: "Deneme Takip Kartı", desc: "Deneme kartı oluştur / onayla / yayınla", icon: FileText, color: "bg-violet-100 text-violet-600", testid: "mod-deneme-karti" },
  { to: "/ozet-ezber", label: "Özet & Ezber", desc: "Ünite ilerlemesi ve ünite sınavları", icon: BookText, color: "bg-indigo-100 text-indigo-600", testid: "mod-ozet-ezber" },
  { to: "/kaynak-kutuphanesi", label: "Kaynak Kütüphanesi", desc: "Kaynak tanımlama, atama ve bölüm takibi", icon: FolderOpen, color: "bg-teal-100 text-teal-600", testid: "mod-kaynak" },
  { to: "/admin/toplu-degerlendirme", label: "Sınıf Toplu Giriş", desc: "Aylık değerlendirmeyi sınıf geneli doldur", icon: Users, color: "bg-fuchsia-100 text-fuchsia-600", testid: "mod-bulk-eval" },
  { to: "/aylik-degerlendirme", label: "Aylık Değerlendirme", desc: "5 bölümlü aylık değerlendirme girişi", icon: CalendarCheck2, color: "bg-orange-100 text-orange-600", testid: "mod-monthly" },
  { to: "/okuma-kitaplari", label: "Okuma Kitapları", desc: "Kitap listesi ve sınav onayları", icon: Library, color: "bg-cyan-100 text-cyan-600", testid: "mod-books" },
  { to: "/denemeler", label: "Zirve Sonuçları", desc: "Deneme sınavı sonuç girişi", icon: Trophy, color: "bg-yellow-100 text-yellow-600", testid: "mod-exams" },
];

export default function AdminDataEntry() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [mode, setMode] = useState("student"); // student | class
  const [studentId, setStudentId] = useState("");
  const [clsGrade, setClsGrade] = useState(0);
  const [clsSection, setClsSection] = useState("");

  useEffect(() => {
    api.get("/students").then(({ data }) => setStudents(data));
  }, []);

  const student = students.find((s) => s.id === studentId);
  const clsSections = useMemo(
    () => [...new Set(students.filter((s) => !clsGrade || s.grade === clsGrade).map((s) => s.section).filter(Boolean))].sort(),
    [students, clsGrade]
  );
  const classCount = students.filter((s) => s.grade === clsGrade && (!clsSection || s.section === clsSection)).length;

  const open = (m) => {
    let query = "";
    if (mode === "student" && student) {
      query = `?student=${student.id}&name=${encodeURIComponent(student.name)}`;
    } else if (mode === "class" && clsGrade) {
      query = `?grade=${clsGrade}${clsSection ? `&section=${encodeURIComponent(clsSection)}` : ""}`;
    }
    navigate(`${m.to}${query}`);
  };

  const sel = "rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";

  return (
    <div className="mx-auto max-w-[1200px] space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600"><Database className="h-6 w-6" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Öğrenci Veri Girişi</h1>
          <p className="text-slate-500">Öğrenci veya sınıf seçip veri girmek istediğiniz paneli açın — girdiğiniz veriler anında öğrenci/veli ekranına yansır.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-3">
          <label className="block text-sm font-semibold text-slate-700">1. Kim İçin Veri Gireceksiniz?</label>
          <div className="flex rounded-xl bg-slate-100 p-1">
            <button data-testid="mode-student-btn" onClick={() => setMode("student")} className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-bold transition ${mode === "student" ? "bg-indigo-600 text-white shadow" : "text-slate-500 hover:text-slate-700"}`}>
              <UserRound className="h-4 w-4" /> Tek Öğrenci
            </button>
            <button data-testid="mode-class-btn" onClick={() => setMode("class")} className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-bold transition ${mode === "class" ? "bg-indigo-600 text-white shadow" : "text-slate-500 hover:text-slate-700"}`}>
              <Users className="h-4 w-4" /> Tüm Sınıf
            </button>
          </div>
        </div>

        {mode === "student" ? (
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-72 flex-1">
              <ClassStudentSelect students={students} value={studentId} onChange={setStudentId} testid="entry-student-select" placeholder="Öğrenci seçiniz (opsiyonel)…" />
            </div>
            {student && (
              <span data-testid="selected-student-chip" className="flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
                <UserRound className="h-4 w-4" /> {student.name} · {student.grade}. Sınıf{student.section ? `/${student.section}` : ""}
              </span>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <select data-testid="class-grade-select" value={clsGrade} onChange={(e) => setClsGrade(Number(e.target.value))} className={sel}>
              <option value={0}>Sınıf seçiniz…</option>
              {GRADE_GROUPS.map((grp) => <optgroup key={grp.label} label={grp.label}>{grp.grades.map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}</optgroup>)}
            </select>
            <select data-testid="class-section-select" value={clsSection} onChange={(e) => setClsSection(e.target.value)} className={sel}>
              <option value="">Tüm Şubeler</option>
              {clsSections.map((s) => <option key={s} value={s}>{s} Şubesi</option>)}
            </select>
            {clsGrade > 0 && (
              <span data-testid="selected-class-chip" className="flex items-center gap-2 rounded-full bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700">
                <Users className="h-4 w-4" /> {clsGrade}. Sınıf{clsSection ? `/${clsSection}` : " (tüm şubeler)"} · {classCount} öğrenci
              </span>
            )}
          </div>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <label className="block text-sm font-semibold text-slate-700">2. Panel Aç (Veri Girişi Yap)</label>
          <span className="text-xs text-slate-400">İstediğiniz panele tıklayarak doğrudan veri girişine başlayabilirsiniz</span>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {MODULES.map((m) => {
            const Icon = m.icon;
            return (
              <button
                key={m.testid}
                data-testid={m.testid}
                onClick={() => open(m)}
                className="group flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-lg active:scale-95"
              >
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${m.color}`}><Icon className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-slate-800">{m.label}</span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-indigo-500" />
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">{m.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
